import { formatWallTime } from '@nudool/shared';
import { CalendarIcon } from 'lucide-react';
import * as React from 'react';

import { DimeButton } from './DimeButton.js';
import { DimeCalendar } from './DimeCalendar.js';
import { DimeInput } from './DimeInput.js';
import { DimeLabel } from './DimeLabel.js';
import { DimePopover, DimePopoverContent, DimePopoverTrigger } from './DimePopover.js';
import { cn } from './lib/utils.js';
import { formatLocalDate, validDateOrUndefined } from './lib/ymd-date.js';

export interface DimeDateTimePickerProps {
  value: Date | undefined;
  onValueChange: (value: Date | undefined) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  /** Time applied when a day is picked before any time is set, as `HH:mm`. */
  defaultTime?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  calendarProps?: Omit<React.ComponentProps<typeof DimeCalendar>, 'mode' | 'selected' | 'onSelect'>;
}

function toTimeString(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function withTime(date: Date, time: string): Date {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  const next = new Date(date.getTime());
  next.setHours(hours, minutes, 0, 0);
  return next;
}

/**
 * @deprecated Picks a viewer-local `Date`, so a time for a place in another zone drifts by the
 * viewer's offset. A place time is a `DimeDatePicker valueFormat="ymd"` plus a time input and a
 * zone, as the event wizard builds it.
 */
export function DimeDateTimePicker({
  value: rawValue,
  onValueChange,
  onBlur,
  placeholder = 'Pick a date and time',
  disabled,
  className,
  id,
  defaultTime = '09:00',
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  calendarProps,
}: Readonly<DimeDateTimePickerProps>) {
  const [open, setOpen] = React.useState(false);
  const timeInputId = React.useId();
  // An Invalid Date reads as no value, so the trigger shows the placeholder instead of throwing.
  const value = validDateOrUndefined(rawValue);
  const time = value ? toTimeString(value) : defaultTime;
  const displayText = value
    ? `${formatLocalDate(value)}, ${formatWallTime(toTimeString(value))}`
    : placeholder;

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) onBlur?.();
  }

  return (
    <DimePopover open={open} onOpenChange={handleOpenChange}>
      <DimePopoverTrigger asChild>
        <DimeButton
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          className={cn(
            'w-full justify-start text-left font-normal',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="mr-2 size-4 shrink-0" />
          <span className="truncate">{displayText}</span>
        </DimeButton>
      </DimePopoverTrigger>
      <DimePopoverContent className="w-auto p-0" align="start">
        <DimeCalendar
          mode="single"
          selected={value}
          defaultMonth={value}
          onSelect={(day) => {
            if (day) onValueChange(withTime(day, time));
          }}
          {...calendarProps}
        />
        <div className="flex items-end gap-2 border-t p-3">
          <div className="flex flex-1 flex-col gap-1">
            <DimeLabel htmlFor={timeInputId}>Time</DimeLabel>
            <DimeInput
              id={timeInputId}
              type="time"
              step={900}
              value={time}
              disabled={!value}
              onChange={(event) => {
                if (value && event.target.value) onValueChange(withTime(value, event.target.value));
              }}
            />
          </div>
          <DimeButton type="button" size="sm" onClick={() => handleOpenChange(false)}>
            Done
          </DimeButton>
        </div>
      </DimePopoverContent>
    </DimePopover>
  );
}
